import { useState } from "react";
import { API } from "@/lib/api";
import { Check, Eye, EyeOff, Loader2, ShieldAlert, X } from "lucide-react";

const FONT_MAIN = "'Hanken Grotesk', 'Inter', sans-serif";

const inputStyle = {
  background: "rgba(255, 255, 255, 0.1)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  color: "#e1e2ed",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.3)",
};
const inputClass = "w-full rounded-xl px-4 py-3 pr-14 text-sm focus:outline-none focus:border-[#b4c5ff] focus:ring-1 focus:ring-[#b4c5ff] transition-all placeholder:text-[#c3c6d7]/70";

export function senhaForte(s) {
  if (s.length < 8) return false;
  if (!/[A-Za-z]/.test(s) || !/\d/.test(s)) return false;
  if (/^(.)\1+$/.test(s)) return false;
  const seqs = ["0123456789", "abcdefghijklmnopqrstuvwxyz", "qwertyuiop", "asdfghjkl", "zxcvbnm"];
  const low = s.toLowerCase();
  return !seqs.some((q) => q.includes(low) || [...q].reverse().join("").includes(low));
}

function SenhaInput({ id, label, value, onChange, show, toggle, testid }) {
  return (
    <div>
      <label htmlFor={id} className="block text-[11px] font-semibold uppercase tracking-wider mb-1"
        style={{ color: "#c3c6d7", fontFamily: FONT_MAIN }}>{label}</label>
      <div className="relative">
        <input id={id} type={show ? "text" : "password"} data-testid={testid} value={value}
          onChange={(e) => onChange(e.target.value)} placeholder="••••••••" enterKeyHint="done"
          className={inputClass} style={inputStyle} />
        <button type="button" data-testid={`${testid}-toggle`} onClick={toggle}
          aria-label={show ? "Ocultar senha" : "Mostrar senha"}
          className="absolute right-1 top-1/2 -translate-y-1/2 z-10 w-11 h-11 flex items-center justify-center rounded-full active:bg-white/10 transition-colors"
          style={{ color: "#8d90a0", touchAction: "manipulation" }}>
          {show ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

export default function CadastrarSenhaModal({ origem, eql, cpf, onClose, onSuccess }) {
  const [nova, setNova] = useState("");
  const [confirma, setConfirma] = useState("");
  const [showN, setShowN] = useState(false);
  const [showC, setShowC] = useState(false);
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  const regras = [
    { ok: nova.length >= 8, texto: "Mínimo de 8 caracteres" },
    { ok: /[A-Za-z]/.test(nova) && /\d/.test(nova), texto: "Letras e números" },
    { ok: nova.length > 0 && nova === confirma, texto: "Confirmação igual à senha" },
  ];

  const salvar = async () => {
    setErro("");
    if (!senhaForte(nova)) {
      setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências (ex.: 123456).");
      return;
    }
    if (nova !== confirma) {
      setErro("A confirmação não confere com a nova senha.");
      return;
    }
    setSalvando(true);
    try {
      const r = await fetch(`${API}/public/cadastrar-senha`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origem, eql, cpf, senha_nova: nova }),
      });
      const d = await r.json();
      if (d.ok) {
        onSuccess();
      } else if (d.motivo === "senha_fraca") {
        setErro("Senha fraca. Use ao menos 8 caracteres com letras e números, sem sequências.");
      } else if (d.motivo === "cadastro_nao_encontrado") {
        setErro("Cadastro não localizado. Verifique os dados informados.");
      } else {
        setErro("Integração indisponível no momento. Tente novamente.");
      }
    } catch {
      setErro("Falha de comunicação. Tente novamente.");
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-6" data-testid="appmobile-cadsenha-modal"
      style={{ fontFamily: FONT_MAIN }}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.7)" }} onClick={() => !salvando && onClose()} />
      <div className="relative w-full max-w-sm rounded-[14px] overflow-hidden shadow-2xl"
        style={{
          background: "rgba(40, 42, 50, 0.85)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
          border: "1px solid rgba(141, 144, 160, 0.5)", borderTop: "1px solid rgba(255, 255, 255, 0.1)",
        }}>
        <span className="absolute top-0 left-[6%] right-[6%] h-px pointer-events-none"
          style={{ background: "linear-gradient(90deg, transparent, rgba(178, 205, 245, 0.85), transparent)", boxShadow: "0 0 10px rgba(120, 165, 230, 0.45)", borderRadius: 999 }} />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-1">
            <h2 className="text-base font-bold" style={{ color: "#e1e2ed" }} data-testid="appmobile-cadsenha-titulo">
              Cadastrar Senha de acesso
            </h2>
            <button type="button" data-testid="appmobile-cadsenha-fechar" aria-label="Fechar"
              onClick={() => !salvando && onClose()}
              className="w-9 h-9 flex items-center justify-center rounded-xl active:bg-white/10"
              style={{ color: "#c3c6d7", touchAction: "manipulation" }}>
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-xs mb-4" style={{ color: "#8d90a0" }}>
            Sua senha atual é fraca ou padrão. Crie uma senha forte para continuar.
          </p>

          <div className="flex flex-col gap-3">
            <SenhaInput id="nova-senha" label="Nova senha" value={nova} onChange={setNova}
              show={showN} toggle={() => setShowN(!showN)} testid="appmobile-cadsenha-nova" />
            <SenhaInput id="confirma-senha" label="Confirmar nova senha" value={confirma} onChange={setConfirma}
              show={showC} toggle={() => setShowC(!showC)} testid="appmobile-cadsenha-confirma" />

            <ul className="flex flex-col gap-1" data-testid="appmobile-cadsenha-regras">
              {regras.map((r) => (
                <li key={r.texto} className="flex items-center gap-1.5 text-[11px]"
                  style={{ color: r.ok ? "#7ef0a0" : "#8d90a0" }}>
                  <Check className="h-3.5 w-3.5" style={{ opacity: r.ok ? 1 : 0.3 }} /> {r.texto}
                </li>
              ))}
            </ul>

            {erro && (
              <div data-testid="appmobile-cadsenha-erro"
                className="flex items-start gap-2 text-xs rounded-xl px-3 py-2.5"
                style={{ background: "rgba(255, 180, 171, 0.08)", color: "#ffb4ab", border: "1px solid rgba(255, 180, 171, 0.3)" }}>
                <ShieldAlert className="h-4 w-4 shrink-0 mt-px" />
                <span>{erro}</span>
              </div>
            )}

            <button type="button" data-testid="appmobile-cadsenha-salvar" onClick={salvar} disabled={salvando}
              className="w-full py-3 rounded-xl text-sm font-semibold text-white active:scale-[0.98] transition-all disabled:opacity-40"
              style={{ background: "#2563EB", boxShadow: "0 0 15px rgba(37,99,235,0.3)" }}>
              {salvando ? <Loader2 className="h-5 w-5 animate-spin mx-auto" /> : "Cadastrar nova senha"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
